import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { LoginModal } from './components/LoginModal';
import { RegisterModal } from './components/RegisterModal';
import apiService from './services/ApiService';
import Home from './components/Home';
import UserProfile from './components/UserProfile';
import GarageRedirect from './components/GarageRedirect';
import VehiclePage from './components/VehiclePage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { loginCancelDestination } from './utils/authRedirect';
import { useShellChrome } from './hooks/useShellChrome';
import { ToastProvider } from './contexts/ToastContext';
import { AuthSessionToasts } from './components/AuthSessionToasts';
import ConditionalAppHeader from './components/ConditionalAppHeader';
import { SessionWarning } from './components/SessionWarning';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { ProtectedOrganizerRoute } from './components/ProtectedOrganizerRoute';
import EventsPage from './components/events/EventsPage';
import PublicEventPage from './components/events/PublicEventPage';
import LegacyEventRedirect from './components/events/LegacyEventRedirect';
import EventRegistrationForm from './components/events/EventRegistrationForm';
import EventArchivePage from './components/events/EventArchivePage';
import PaymentReturnPage from './components/events/PaymentReturnPage';
import SpectatorTicketsPage from './components/events/SpectatorTicketsPage';
import SpectatorPaymentReturnPage from './components/events/SpectatorPaymentReturnPage';
import { ProtectedJudgeRoute } from './components/ProtectedJudgeRoute';
import { InvitationLandingPage } from './components/InvitationLandingPage';
import { RegistrationSuccessModal } from './components/RegistrationSuccessModal';
import TermsOfServicePage from './components/legal/TermsOfServicePage';
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage';
import CommunityGuidelinesPage from './components/legal/CommunityGuidelinesPage';
import CookiePolicyPage from './components/legal/CookiePolicyPage';
import CopyrightDmcaPage from './components/legal/CopyrightDmcaPage';
import VerifyEmailPage from './components/auth/VerifyEmailPage';
import NativeSessionBootstrapPage from './pages/NativeSessionBootstrapPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import UnsubscribePage from './components/auth/UnsubscribePage';
import PrivacyCenterPage from './components/privacy/PrivacyCenterPage';
import CookieConsentBanner from './components/privacy/CookieConsentBanner';
import { BodyScrollRepair } from './components/BodyScrollRepair';
import DiscoverPage from './components/DiscoverPage';
import FavoriteBuildsPage from './components/FavoriteBuildsPage';
import ClubsDirectoryPage from './components/clubs/ClubsDirectoryPage';
import ClubProfilePage from './components/clubs/ClubProfilePage';
import ClubCreatePage from './components/clubs/ClubCreatePage';
import ClubManagePage from './components/clubs/ClubManagePage';
import BadgeDetailPage from './components/badges/BadgeDetailPage';
import GlobalSearchResultsPage from './components/search/GlobalSearchResults';
import MyTicketsPage from './components/events/MyTicketsPage';
import ShopDirectoryPage from './components/shops/ShopDirectoryPage';
import ShopProfilePage from './components/shops/ShopProfilePage';
import NotificationPreferencesPage from './components/notifications/NotificationPreferencesPage';
import NotificationsPage from './components/notifications/NotificationsPage';
import BillingSettingsPage from './components/premium/BillingSettingsPage';
import SettingsHubPage from './components/settings/SettingsHubPage';
import PassportPage from './components/passport/PassportPage';
import PassportEncounterDetail from './components/passport/PassportEncounterDetail';
import PassportCollectionsPage from './components/passport/PassportCollectionsPage';
import PassportCampaignsPage from './components/passport/PassportCampaignsPage';
import PassportCampaignDetailPage from './components/passport/PassportCampaignDetailPage';
import PassportRecapPage from './components/passport/PassportRecapPage';
import PassportScanLandingPage from './components/passport/PassportScanLandingPage';
import PassportPrivacyPage from './components/passport/PassportPrivacyPage';
import FoundingVehiclesPage from './components/FoundingVehiclesPage';
import { NativeShellSessionSync } from './components/NativeShellSessionSync';
import { setNoIndexMeta } from './utils/metaTags';
import PageLoader from './components/ui/PageLoader';
import {
  LazyAdminLayout,
  LazyAdminDashboard,
  LazyUserManagement,
  LazyRegistrationCodeManagement,
  LazyActivityLog,
  LazyAdminEvents,
  LazyAdminAiDashboard,
  LazyAdminClubsReview,
  LazyAdminVehicleCertifications,
  LazyAdminFeaturedBuilds,
  LazyAdminSponsorship,
  LazyAdminProductUpdates,
  LazyAdminTestimonials,
  LazyAdminAchievements,
  LazyAdminPerformanceModeration,
  LazyAdminFactorySpecs,
  LazyAdminBuildMedia,
  LazyAdminShopModeration,
  LazyAdminPrivacyRequests,
  LazyAdminPayments,
  LazyAdminPremium,
  LazyAdminBadgeModeration,
  LazyAdminPassport,
  LazyAdminEmail,
  LazyAdminMaps,
  LazyAdminLimitedDesignations,
  LazyAdminFeatureFlags,
  LazyOrganizerLayout,
  LazyOrganizerDashboard,
  LazyCreateEventForm,
  LazyEventAdmin,
  LazyEventCheckin,
  LazyGateScanPage,
  LazyOrganizerPaymentsPage,
  LazyJudgeLayout,
  LazyJudgeDashboard,
  LazyJudgeEventVehicles,
  LazyJudgeScorecard,
} from './routes/lazyOpsRoutes';

function LoginRouteModal() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const returnTo = (location.state as { from?: string } | null)?.from || '/';

  // Hide native Scan FAB while the login sheet is up (and restore on leave).
  useShellChrome({ hideScan: true });

  const handleClose = () => {
    setOpen(false);
    const dest = loginCancelDestination(returnTo);
    navigate(`${dest.pathname}${dest.search}`, {
      replace: true,
      state: { authPromptDismissed: true },
    });
  };

  const handleLogin = () => {
    setOpen(false);
    navigate(returnTo === '/login' ? '/' : returnTo);
  };

  useEffect(() => {
    setOpen(true);
  }, []);

  // Already signed in (e.g. native bootstrap race bounced here) — never flash login.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const dest = returnTo === '/login' || returnTo === '/register' ? '/' : returnTo;
    navigate(dest, { replace: true });
  }, [authLoading, isAuthenticated, navigate, returnTo]);

  if (authLoading || isAuthenticated) {
    return null;
  }

  return (
    <LoginModal
      isOpen={open}
      onClose={handleClose}
      onLogin={handleLogin}
      onSwitchToRegister={() => navigate('/register')}
    />
  );
}

function RegisterRouteModal({ onRegisterSuccess }: { onRegisterSuccess: () => void | Promise<void> }) {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
    navigate('/');
  };

  const handleRegister = async () => {
    setOpen(false);
    await onRegisterSuccess();
    navigate('/');
  };

  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <RegisterModal
      isOpen={open}
      onClose={handleClose}
      onRegister={handleRegister}
      onSwitchToLogin={() => navigate('/login')}
    />
  );
}

const App = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isRegistrationSuccessModalOpen, setIsRegistrationSuccessModalOpen] = useState(false);
  const [registrationSuccessData, setRegistrationSuccessData] = useState<{
    username: string;
    firstName: string;
  } | null>(null);

  const handleLogin = () => {
    setIsLoginModalOpen(false);
  };

  const handleRegister = async () => {
    setIsRegisterModalOpen(false);
    try {
      const currentUser = apiService.getCurrentUser();
      if (currentUser) {
        setRegistrationSuccessData({
          username: currentUser.username,
          firstName: currentUser.first_name,
        });
        setIsRegistrationSuccessModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to get current user data:', error);
    }
  };

  const handleSwitchToRegister = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  return (
    <ToastProvider>
      <AuthProvider>
        <AuthSessionToasts />
        <SessionWarning />
        <Router>
          <NativeShellSessionSync />
          <ConditionalAppHeader />
          <main id="main-content">
          <Suspense fallback={<PageLoader message="Loading…" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route
              path="/invite"
              element={
                <InvitationLandingPage
                  onRegisterClick={(code) => {
                    sessionStorage.setItem('inviteCode', code);
                    setIsRegisterModalOpen(true);
                  }}
                />
              }
            />
            <Route path="/login" element={<LoginRouteModal />} />
            <Route path="/register" element={<RegisterRouteModal onRegisterSuccess={handleRegister} />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/copyright" element={<CopyrightDmcaPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/native/session" element={<NativeSessionBootstrapPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <LazyAdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route index element={<LazyAdminDashboard />} />
              <Route path="users" element={<LazyUserManagement />} />
              <Route path="events" element={<LazyAdminEvents />} />
              <Route path="ai" element={<LazyAdminAiDashboard />} />
              <Route path="codes" element={<LazyRegistrationCodeManagement />} />
              <Route path="clubs" element={<LazyAdminClubsReview />} />
              <Route path="certifications" element={<LazyAdminVehicleCertifications />} />
              <Route path="featured" element={<LazyAdminFeaturedBuilds />} />
              <Route path="sponsorship" element={<LazyAdminSponsorship />} />
              <Route path="achievements" element={<LazyAdminAchievements />} />
              <Route path="expansion" element={<Navigate to="/admin/performance" replace />} />
              <Route path="performance" element={<LazyAdminPerformanceModeration />} />
              <Route path="factory" element={<LazyAdminFactorySpecs />} />
              <Route path="media" element={<LazyAdminBuildMedia />} />
              <Route path="shops" element={<LazyAdminShopModeration />} />
              <Route path="privacy" element={<LazyAdminPrivacyRequests />} />
              <Route path="badges" element={<LazyAdminBadgeModeration />} />
              <Route path="limited-designations" element={<LazyAdminLimitedDesignations />} />
              <Route path="feature-flags" element={<LazyAdminFeatureFlags />} />
              <Route path="payments" element={<LazyAdminPayments />} />
              <Route path="premium" element={<LazyAdminPremium />} />
              <Route path="updates" element={<LazyAdminProductUpdates />} />
              <Route path="testimonials" element={<LazyAdminTestimonials />} />
              <Route path="passport" element={<LazyAdminPassport />} />
              <Route path="email" element={<LazyAdminEmail />} />
              <Route path="maps" element={<LazyAdminMaps />} />
              <Route path="activity" element={<LazyActivityLog />} />
            </Route>
            <Route path="/garage" element={<GarageRedirect />} />
            <Route path="/favorites" element={<FavoriteBuildsPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/founding-vehicles" element={<FoundingVehiclesPage />} />
            <Route path="/search" element={<GlobalSearchResultsPage />} />
            <Route path="/shops" element={<ShopDirectoryPage />} />
            <Route path="/shops/:slug" element={<ShopProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsHubPage />} />
            <Route path="/settings/notifications" element={<NotificationPreferencesPage />} />
            <Route path="/settings/billing" element={<BillingSettingsPage />} />
            <Route path="/settings/privacy" element={<PrivacyCenterPage />} />
            <Route path="/passport" element={<PassportPage />} />
            <Route path="/passport/s/:token" element={<PassportScanLandingPage />} />
            <Route path="/passport/settings" element={<PassportPrivacyPage />} />
            <Route path="/passport/collections" element={<PassportCollectionsPage />} />
            <Route path="/passport/campaigns" element={<PassportCampaignsPage />} />
            <Route path="/passport/campaigns/:slug" element={<PassportCampaignDetailPage />} />
            <Route path="/passport/recap" element={<PassportRecapPage />} />
            <Route path="/passport/encounters/:publicId" element={<PassportEncounterDetail />} />
            <Route path="/clubs" element={<ClubsDirectoryPage />} />
            <Route path="/clubs/new" element={<ClubCreatePage />} />
            <Route path="/clubs/:slug/manage" element={<ClubManagePage />} />
            <Route path="/clubs/:slug" element={<ClubProfilePage />} />
            <Route path="/badges/:publicId" element={<BadgeDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:organizerSlug/:eventSlug/register" element={<EventRegistrationForm />} />
            <Route path="/events/:organizerSlug/:eventSlug/payment/return" element={<PaymentReturnPage />} />
            <Route path="/events/:organizerSlug/:eventSlug/tickets/return" element={<SpectatorPaymentReturnPage />} />
            <Route path="/events/:organizerSlug/:eventSlug/tickets" element={<SpectatorTicketsPage />} />
            <Route path="/events/:organizerSlug/:eventSlug/archive" element={<EventArchivePage />} />
            <Route path="/events/:organizerSlug/:eventSlug" element={<PublicEventPage />} />
            <Route path="/events/:slug/register" element={<LegacyEventRedirect />} />
            <Route path="/events/:slug/payment/return" element={<LegacyEventRedirect />} />
            <Route path="/events/:slug/tickets/return" element={<LegacyEventRedirect />} />
            <Route path="/events/:slug/tickets" element={<LegacyEventRedirect />} />
            <Route path="/tickets" element={<MyTicketsPage />} />
            <Route path="/events/:slug/archive" element={<LegacyEventRedirect />} />
            <Route path="/events/:slug" element={<LegacyEventRedirect />} />
            <Route
              path="/organizer"
              element={
                <ProtectedOrganizerRoute>
                  <LazyOrganizerLayout />
                </ProtectedOrganizerRoute>
              }
            >
              <Route index element={<LazyOrganizerDashboard />} />
              <Route path="payments" element={<LazyOrganizerPaymentsPage />} />
              <Route path="events/new" element={<LazyCreateEventForm />} />
              <Route path="events/:eventId/checkin" element={<LazyEventCheckin />} />
              <Route path="events/:eventId/gate" element={<LazyGateScanPage />} />
              <Route path="events/:eventId" element={<LazyEventAdmin />} />
            </Route>
            <Route
              path="/judge"
              element={
                <ProtectedJudgeRoute>
                  <LazyJudgeLayout />
                </ProtectedJudgeRoute>
              }
            >
              <Route index element={<LazyJudgeDashboard />} />
              <Route path="events/:eventId" element={<LazyJudgeEventVehicles />} />
              <Route path="events/:eventId/score/:registrationId" element={<LazyJudgeScorecard />} />
            </Route>
            <Route path="/v/:username/:slug" element={<LegacyVehicleRedirect />} />
            <Route path=":username/:vehicleSlug" element={<VehiclePage />} />
            <Route path=":username" element={<UsernameRouteWrapper />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </main>

          {isLoginModalOpen && (
            <LoginModal
              isOpen={isLoginModalOpen}
              onClose={() => setIsLoginModalOpen(false)}
              onLogin={handleLogin}
              onSwitchToRegister={handleSwitchToRegister}
            />
          )}

          {isRegisterModalOpen && (
            <RegisterModal
              isOpen={isRegisterModalOpen}
              onClose={() => setIsRegisterModalOpen(false)}
              onRegister={handleRegister}
              onSwitchToLogin={handleSwitchToLogin}
            />
          )}

          {isRegistrationSuccessModalOpen && (
            <RegistrationSuccessModal
              isOpen={isRegistrationSuccessModalOpen}
              onClose={() => {
                setIsRegistrationSuccessModalOpen(false);
                setIsRegisterModalOpen(false);
              }}
              userDetails={registrationSuccessData || undefined}
            />
          )}
          <CookieConsentBanner />
          <BodyScrollRepair />
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
};

function LegacyVehicleRedirect() {
  const { username, slug } = useParams<{ username: string; slug: string }>();
  const location = useLocation();
  if (!username || !slug) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/${username}/${slug}${location.search}`} replace />;
}

function UsernameRouteWrapper() {
  const { username } = useParams();
  if (!username || RESERVED_USERNAMES.includes(username.toLowerCase())) {
    return <NotFound />;
  }
  return <UserProfile />;
}

const RESERVED_USERNAMES = [
  'login',
  'register',
  'admin',
  'v',
  'garage',
  'events',
  'organizer',
  'judge',
  'discover',
  'clubs',
  'badges',
  'invite',
  'favorites',
  'shops',
  'search',
  'settings',
  'passport',
  'terms',
  'privacy',
  'cookies',
  'copyright',
  'unsubscribe',
  'verify-email',
  'forgot-password',
  'reset-password',
  'community-guidelines',
];

function NotFound() {
  useEffect(() => {
    setNoIndexMeta('Page not found | Bilt:Spec');
  }, []);

  return (
    <div className="page flex items-center justify-center">
      <div className="text-center px-4 animate-fade-in">
        <p className="text-6xl font-bold text-orange-300 mb-2">404</p>
        <h1 className="heading-section mb-3">This page isn’t here</h1>
        <p className="text-muted mb-8 max-w-sm mx-auto">
          It may have moved, or the link is old.
        </p>
        <Link to="/" className="btn-primary">Back to Bilt:Spec</Link>
      </div>
    </div>
  );
}

export default App;
